import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  Directive,
  ElementRef,
  Inject,
  Injectable,
  OnChanges,
  Renderer2,
  SimpleChanges,
  input,
  output,
} from '@angular/core';
import { LocalhostKeys } from '@core/constants';
import { AuthInfo } from '@root/app/authorization/models';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
@Injectable({
  providedIn: 'root',
})
@Directive({
  selector: '[xemexFetchImage]',
  standalone: true,
})
export class FetchImageDirective implements OnChanges {
  src = input<string | undefined>('');
  dummySrc = input<string>('');

  /** Emits `true` once an image blob is loaded, `false` when there is no image to show
   * (missing src, missing token, or a failed fetch). Lets callers fall back to other content. */
  imageResolved = output<boolean>();

  constructor(
    private http: HttpClient,
    private elementRef: ElementRef,
    private ren: Renderer2,
    @Inject('IMAGE_CACHE') private imageCache: Record<string, Observable<Blob>>,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['src']) {
      this.fetchImage();
    }
  }

  fetchImage() {
    this.ren.setAttribute(this.elementRef.nativeElement, 'src', this.dummySrc());

    if (!this.src()) {
      this.imageResolved.emit(false);
      return;
    }
    let loggedUserData: AuthInfo;
    const localData = localStorage.getItem(LocalhostKeys.ACCESS_TOKEN);

    if (!localData) {
      this.imageResolved.emit(false);
      return;
    }
    loggedUserData = JSON.parse(localData);

    const headers = new HttpHeaders({
      'Api-Version': 'v1',
      Authorization: `Bearer ${loggedUserData!.access_token}`,
    });

    if (!this.imageCache) {
      this.imageCache = {};
    }
    const key = '' + this.src();
    let prom;
    if (this.imageCache[key]) {
      prom = this.imageCache[key];
    } else {
      prom = this.http.get(this.src()!, { headers, responseType: 'blob' }).pipe(shareReplay(1));
      this.imageCache[key] = prom;
    }

    prom.subscribe({
      next: blob => {
        // The backend answers "no image" with a 200 carrying a non-image body (e.g. a JSON
        // placeholder). Treat anything that isn't an image as unresolved so callers fall back
        // to their icon/dummy instead of rendering a broken image.
        if (!blob.type.startsWith('image/')) {
          this.imageResolved.emit(false);
          return;
        }
        const objectUrl = URL.createObjectURL(blob);
        this.ren.setAttribute(this.elementRef.nativeElement, 'src', objectUrl);
        this.ren?.addClass?.(this.elementRef.nativeElement, 'max-h-full');
        this.ren?.addClass?.(this.elementRef.nativeElement, 'max-w-full');
        this.imageResolved.emit(true);
      },
      error: () => this.imageResolved.emit(false),
    });
  }
}
